import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { IconLoader2 } from "@tabler/icons-react";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        "primary-gradient":
          "bg-linear-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700",
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "dark:border-border bg-background hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground border border-zinc-200 shadow-xs",
        secondary: [
          "text-secondary-foreground hover:border-primary-500 border border-[#ccc] bg-[#ccc] hover:bg-[#ccc]",
          "dark:text-secondary-foreground dark:hover:border-primary-500 dark:border-[#181818] dark:bg-[#181818] dark:hover:bg-[#24162c] dark:hover:text-[#dfbaf5]",
        ],
        ghost:
          "hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
        link: "text-purple-500 underline duration-200 hover:opacity-75",
        success: "bg-green-500 text-white hover:bg-green-600",
        "outline-pink":
          "border border-pink-500 text-pink-500 hover:bg-pink-500/10",
        "outline-purple":
          "border border-purple-500 text-purple-500 hover:bg-purple-500/10",
      },
      size: {
        sm: "h-8 gap-1.5 px-3",
        md: "h-9 px-4 py-2",
        lg: "h-10 px-4 py-2",
        xl: "h-11 px-5 py-2.5",
        "2xl": "h-12 px-6 py-3 text-base font-semibold",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "lg",
    },
  },
);

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    icon?: React.ReactNode;
    iconOnly?: boolean;
    tooltip?: string;
    loading?: boolean;
    /**
     * Class name for the label
     * Only used when icon is passed
     */
    labelClassName?: string;
  };

function Button({
  className,
  variant,
  size = "lg",
  asChild = false,
  icon,
  iconOnly = false,
  children,
  tooltip,
  loading,
  labelClassName,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  const isIconOnly = iconOnly || !children;

  const renderContent = () => {
    return (
      <>
        {loading ? <IconLoader2 className="animate-spin" /> : icon}
        {!isIconOnly && (
          <>
            {icon ? <div className={labelClassName}>{children}</div> : children}
          </>
        )}
      </>
    );
  };

  if (asChild) {
    return (
      <Comp
        data-slot="button"
        className={cn(
          buttonVariants({
            variant,
            size,
          }),
          {
            "size-8 items-center justify-center p-0 has-[>svg]:px-0":
              isIconOnly && size === "sm",
            "size-9 items-center justify-center p-0 has-[>svg]:px-0":
              isIconOnly && size === "md",
            "size-10 items-center justify-center p-0 has-[>svg]:px-0":
              isIconOnly && size === "lg",
            "size-11 items-center justify-center p-0 has-[>svg]:px-0":
              isIconOnly && size === "xl",
            "size-12 items-center justify-center p-0 has-[>svg]:px-0":
              isIconOnly && size === "2xl",
          },
          className,
        )}
        {...props}
        disabled={loading || props.disabled}
      >
        {children}
      </Comp>
    );
  }

  const ButtonComponent = (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({
          variant,
          size,
        }),
        {
          "size-8 items-center justify-center p-0 has-[>svg]:px-0":
            isIconOnly && size === "sm",
          "size-9 items-center justify-center p-0 has-[>svg]:px-0":
            isIconOnly && size === "md",
          "size-10 items-center justify-center p-0 has-[>svg]:px-0":
            isIconOnly && size === "lg",
          "size-11 items-center justify-center p-0 has-[>svg]:px-0":
            isIconOnly && size === "xl",
          "size-12 items-center justify-center p-0 has-[>svg]:px-0":
            isIconOnly && size === "2xl",
        },
        className,
      )}
      {...props}
      disabled={loading || props.disabled}
    >
      {renderContent()}
    </Comp>
  );

  return (
    <>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{ButtonComponent}</TooltipTrigger>
          <TooltipContent>
            <p className="font-space-grotesk text-xs">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        ButtonComponent
      )}
    </>
  );
}

export { Button, buttonVariants };
